import React, { useState, useEffect } from 'react';
import { Calculator, AlertCircle, TrendingUp, RefreshCw } from 'lucide-react';

export const GoldPriceCalculator = () => {
  const [weight, setWeight] = useState('');
  const [karat, setKarat] = useState('18');
  const [calculationType, setCalculationType] = useState('new');
  const [result, setResult] = useState(null);
  const [goldPrices, setGoldPrices] = useState({
    '18': 0,
    '21': 0,
    '24': 0
  });
  const [loading, setLoading] = useState(false);

  // ✅ دریافت قیمت‌های واقعی از backend (منبع واحد)
  const fetchRealPrices = async (force = false) => {
    try {
      setLoading(true);

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const url = `${API_BASE}/live-gold-prices/${force ? '?refresh=1' : ''}`;
      const response = await fetch(url, { signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error('server error');

      const data = await response.json();
      const price18 = Number(data?.gold18?.price) || 0;
      const price24 = Number(data?.gold24?.price) || 0;
      // ۲۱ عیار را از میانگین می‌سازیم (یا دقیق: 18 × 21/18)
      const price21 = price18 ? Math.round(price18 * 21 / 18) : 0;

      setGoldPrices({
        '18': price18,
        '21': price21,
        '24': price24,
      });
    } catch (error) {
      console.error('Error fetching prices:', error);
      // قیمت‌ها رو صفر می‌گذاریم تا کاربر مطمئن شه دیتا تازه نیست
      setGoldPrices({ '18': 0, '21': 0, '24': 0 });
    } finally {
      setLoading(false);
    }
  };

  // بارگذاری قیمت‌ها هنگام شروع
  useEffect(() => {
    fetchRealPrices();
    // به‌روزرسانی هر ۱ دقیقه (backend خودش ۵ دقیقه cache داره)
    const interval = setInterval(() => fetchRealPrices(false), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const calculatePrice = () => {
    const weightNum = parseFloat(weight);
    
    if (!weightNum || weightNum <= 0) {
      alert('لطفا وزن معتبر وارد کنید');
      return;
    }

    const basePrice = weightNum * goldPrices[karat];
    let finalPrice, makingCost = 0, tax = 0;

    if (calculationType === 'new') {
      makingCost = basePrice * 0.15;
      tax = (basePrice + makingCost) * 0.09;
      finalPrice = basePrice + makingCost + tax;
    } else {
      finalPrice = basePrice * 0.95;
    }

    setResult({
      basePrice,
      makingCost,
      tax,
      finalPrice,
      pricePerGram: goldPrices[karat]
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fa-IR').format(Math.round(price));
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 max-w-2xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center">
          <Calculator className="w-6 h-6 sm:w-8 sm:h-8 text-amber-700/70 ml-3" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">محاسبه قیمت طلا</h2>
        </div>
        
        <button
          onClick={fetchRealPrices}
          disabled={loading}
          className="flex items-center gap-2 text-sm bg-amber-50 hover:bg-amber-100 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
          title="بروزرسانی قیمت‌ها"
        >
          <RefreshCw className={`w-4 h-4 text-amber-600 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-amber-700">بروزرسانی قیمت</span>
        </button>
      </div>

      {/* نمایش قیمت‌های فعلی */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
        {Object.entries(goldPrices).map(([k, price]) => (
          <div key={k} className={`rounded-lg p-2 sm:p-4 text-center min-w-0 overflow-hidden transition-all ${
            karat === k 
              ? 'bg-gradient-to-br from-amber-100 to-yellow-100 border-2 border-amber-400' 
              : 'bg-gradient-to-br from-amber-50 to-yellow-50'
          }`}>
            <h4 className="font-semibold text-sm text-gray-600">{k} عیار</h4>
            <p className={`text-[clamp(0.7rem,3.3vw,1.125rem)] font-bold mt-1 whitespace-nowrap ${karat === k ? 'text-amber-700' : 'text-amber-600'}`}>
              {formatPrice(price)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500">تومان/گرم</p>
          </div>
        ))}
      </div>

      {/* نوع طلا */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3">نوع طلا:</label>
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          <button
            onClick={() => setCalculationType('new')}
            className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
              calculationType === 'new'
                ? 'border-amber-500 bg-amber-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h4 className="font-semibold mb-1">طلای نو</h4>
            <p className="text-sm text-gray-600">با اجرت و مالیات</p>
          </button>
          <button
            onClick={() => setCalculationType('used')}
            className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${
              calculationType === 'used'
                ? 'border-amber-500 bg-amber-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h4 className="font-semibold mb-1">طلای دست دوم</h4>
            <p className="text-sm text-gray-600">5% کسر می‌شود</p>
          </button>
        </div>
      </div>

      {/* ورودی‌ها */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold mb-2">وزن (گرم)</label>
          <input
            type="number"
            step="0.1"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 transition-all"
            placeholder="مثال: 5.5"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">عیار</label>
          <select
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 transition-all"
            value={karat}
            onChange={(e) => setKarat(e.target.value)}
          >
            
            <option value="18">18 عیار</option>
            <option value="21">21 عیار</option>
            <option value="24">24 عیار</option>
            
            
          </select>
        </div>
      </div>

      {/* دکمه محاسبه */}
      <button
        onClick={calculatePrice}
        className="w-full bg-amber-300/70 text-lg text-stone-900 py-3 rounded-lg hover:shadow-lg hover:from-amber-500 hover:to-yellow-600 transition-all transform hover:scale-[1.02]"
      >
        محاسبه قیمت
      </button>

      {/* نتیجه */}
      {result && (
        <div className="mt-6 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 animate-fadeIn border border-amber-200">
          <h3 className="font-semibold mb-4 text-lg flex items-center">
            <TrendingUp className="w-5 h-5 ml-2 text-amber-600" />
            نتیجه محاسبات:
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-2 bg-white/50 rounded">
              <span className="text-gray-700">قیمت هر گرم:</span>
              <span className="font-semibold text-amber-700">
                {formatPrice(result.pricePerGram)} تومان
              </span>
            </div>

            <div className="flex justify-between items-center p-2 bg-white/50 rounded">
              <span className="text-gray-700">وزن:</span>
              <span className="font-semibold">{weight} گرم</span>
            </div>
            
            <div className="flex justify-between items-center p-2 bg-white/50 rounded">
              <span className="text-gray-700">قیمت پایه:</span>
              <span className="font-semibold">{formatPrice(result.basePrice)} تومان</span>
            </div>
            
            {calculationType === 'new' && (
              <>
                <div className="flex justify-between items-center p-2 bg-white/50 rounded">
                  <span className="text-gray-700">اجرت ساخت (15%):</span>
                  <span className="font-semibold text-orange-600">
                    +{formatPrice(result.makingCost)} تومان
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-white/50 rounded">
                  <span className="text-gray-700">مالیات (9%):</span>
                  <span className="font-semibold text-orange-600">
                    +{formatPrice(result.tax)} تومان
                  </span>
                </div>
              </>
            )}
            
            {calculationType === 'used' && (
              <div className="flex justify-between items-center p-2 bg-white/50 rounded">
                <span className="text-gray-700">کسر 5%:</span>
                <span className="font-semibold text-red-600">
                  -{formatPrice(result.basePrice * 0.05)} تومان
                </span>
              </div>
            )}
            
            <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-amber-300">
              <span className="text-lg font-bold text-gray-800">قیمت نهایی:</span>
              <span className="text-xl sm:text-2xl font-bold text-amber-600">
                {formatPrice(result.finalPrice)} تومان
              </span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 flex items-start">
              <AlertCircle className="w-4 h-4 ml-2 mt-0.5 flex-shrink-0" />
              این محاسبات بر اساس قیمت لحظه‌ای طلا انجام شده و ممکن است تغییر کند. 
              قیمت نهایی ممکن است بسته به طلافروش و نوع محصول متفاوت باشد.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoldPriceCalculator;